'use strict';

var codecsCore = require('@solana/codecs-core');
var codecsNumbers = require('@solana/codecs-numbers');

// src/option.ts
var some = (value) => ({ __option: "Some", value });
var none = () => ({ __option: "None" });
var isOption = (input) => !!(input && typeof input === "object" && "__option" in input && (input.__option === "Some" && "value" in input || input.__option === "None"));
var isSome = (option) => option.__option === "Some";
var isNone = (option) => option.__option === "None";

// src/unwrap-option.ts
function unwrapOption(option, fallback) {
  if (isSome(option))
    return option.value;
  return fallback ? fallback() : null;
}
var wrapNullable = (nullable) => nullable !== null ? some(nullable) : none();

// src/option-codec.ts
function getOptionEncoder(item, config = {}) {
  const prefix = config.prefix ?? codecsNumbers.getU8Encoder();
  const fixed = config.fixed ?? false;
  const isZeroSizeItem = codecsCore.isFixedSize(item) && codecsCore.isFixedSize(prefix) && item.fixedSize === 0;
  if (fixed || isZeroSizeItem) {
    codecsCore.assertIsFixedSize(item);
    codecsCore.assertIsFixedSize(prefix);
    const fixedSize = prefix.fixedSize + item.fixedSize;
    return codecsCore.createEncoder({
      fixedSize,
      write: (optionOrNullable, bytes, offset) => {
        const option = isOption(optionOrNullable) ? optionOrNullable : wrapNullable(optionOrNullable);
        const prefixOffset = prefix.write(Number(isSome(option)), bytes, offset);
        if (isSome(option)) {
          item.write(option.value, bytes, prefixOffset);
        }
        return offset + fixedSize;
      }
    });
  }
  return codecsCore.createEncoder({
    getSizeFromValue: (optionOrNullable) => {
      const option = isOption(optionOrNullable) ? optionOrNullable : wrapNullable(optionOrNullable);
      return codecsCore.getEncodedSize(Number(isSome(option)), prefix) + (isSome(option) ? codecsCore.getEncodedSize(option.value, item) : 0);
    },
    maxSize: sumCodecSizes([prefix, item].map(getMaxSize)) ?? void 0,
    write: (optionOrNullable, bytes, offset) => {
      const option = isOption(optionOrNullable) ? optionOrNullable : wrapNullable(optionOrNullable);
      offset = prefix.write(Number(isSome(option)), bytes, offset);
      if (isSome(option)) {
        offset = item.write(option.value, bytes, offset);
      }
      return offset;
    }
  });
}
function getOptionDecoder(item, config = {}) {
  const prefix = config.prefix ?? codecsNumbers.getU8Decoder();
  const fixed = config.fixed ?? false;
  let fixedSize = null;
  const isZeroSizeItem = codecsCore.isFixedSize(item) && codecsCore.isFixedSize(prefix) && item.fixedSize === 0;
  if (fixed || isZeroSizeItem) {
    codecsCore.assertIsFixedSize(item);
    codecsCore.assertIsFixedSize(prefix);
    fixedSize = prefix.fixedSize + item.fixedSize;
  }
  return codecsCore.createDecoder({
    ...fixedSize === null ? { maxSize: sumCodecSizes([prefix, item].map(getMaxSize)) ?? void 0 } : { fixedSize },
    read: (bytes, offset) => {
      if (bytes.length - offset <= 0) {
        return [none(), offset];
      }
      const [isSome2, prefixOffset] = prefix.read(bytes, offset);
      if (isSome2 === 0) {
        return [none(), fixedSize !== null ? offset + fixedSize : prefixOffset];
      }
      const [value, newOffset] = item.read(bytes, prefixOffset);
      return [some(value), fixedSize !== null ? offset + fixedSize : newOffset];
    }
  });
}
function getOptionCodec(item, config = {}) {
  return codecsCore.combineCodec(getOptionEncoder(item, config), getOptionDecoder(item, config));
}
function sumCodecSizes(sizes) {
  return sizes.reduce((all, size) => all === null || size === null ? null : all + size, 0);
}
function getMaxSize(codec) {
  return codecsCore.isFixedSize(codec) ? codec.fixedSize : codec.maxSize ?? null;
}

// src/unwrap-option-recursively.ts
function unwrapOptionRecursively(input, fallback) {
  if (!input || ArrayBuffer.isView(input)) {
    return input;
  }
  const next = (x) => fallback ? unwrapOptionRecursively(x, fallback) : unwrapOptionRecursively(x);
  if (isOption(input)) {
    if (isSome(input))
      return next(input.value);
    return fallback ? fallback() : null;
  }
  if (Array.isArray(input)) {
    return input.map(next);
  }
  if (typeof input === "object") {
    return Object.fromEntries(Object.entries(input).map(([k, v]) => [k, next(v)]));
  }
  return input;
}

exports.getOptionCodec = getOptionCodec;
exports.getOptionDecoder = getOptionDecoder;
exports.getOptionEncoder = getOptionEncoder;
exports.isNone = isNone;
exports.isOption = isOption;
exports.isSome = isSome;
exports.none = none;
exports.some = some;
exports.unwrapOption = unwrapOption;
exports.unwrapOptionRecursively = unwrapOptionRecursively;
exports.wrapNullable = wrapNullable;
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.browser.cjs.map